import type { KitSpec } from '@/config/cosmetics';
import type { BodyFit, FlankMap, LiveryPainter, LiveryViews } from './types';
import { css, flankCover, luma, mixColor, planCover, rgba } from './types';
import { dragonPainter } from './dragon';

/**
 * livery/kits.ts (M144) — the football kits, painted the way the CAR would
 * wear the shirt.
 *
 * THE DESIGN RULE: the body is the jersey, the cabin is the neck.
 *
 *   TOP + ENDS are the shirt WORN. The front panel lies over the hood and
 *   falls down the nose; the back panel lies over the deck and falls down
 *   the tail. So stripes run nose → tail like a striped back seen from the
 *   chase camera, hoops and chest bands cross the hood, a centre band is a
 *   racing stripe, and a yoke sits on the hood under the windscreen.
 *
 *   The FLANKS are the shirt SHOWN: each door is a billboard of the shirt
 *   front, so stripes stand upright on it, hoops and chest bands run along
 *   it and the sash falls across it.
 *
 *   The COLLAR is a ring round the base of the glass — the classic beltline
 *   stripe — with the shirt's real neck (crew, V, wrap-over, polo, open
 *   collar) cut into the hood under the windscreen. The CUFFS are the wheel
 *   arches (the fenders are the sleeves). Shorts and socks are not painted:
 *   they dress the trim parts and the rims.
 *
 * Everything is drawn in METRES at one garment scale, so the same shirt
 * reads as the same fabric on the coupe, the monster truck and the bike.
 */

type G = CanvasRenderingContext2D;
type Pt = [number, number];

/** The car width one shirt chest stands for: stripe counts are per chest. */
const CHEST = 1.65;
/** The run a shirt's collar-to-hem stands for on the hood / deck panels. */
const TORSO = 2.3;
const BIG = 50; // "everything", in metres

/** A labelled test card for every view: proves which surface reads which
 *  view, which way each view runs, and that a metre is a metre. */
export const debugPainter: LiveryPainter = (v: LiveryViews) => {
  const { L, H, W } = v.fit;
  const grid = (g: G, w: number, h: number, bg: string, label: string): void => {
    g.fillStyle = bg;
    g.fillRect(0, 0, w, h);
    g.fillStyle = 'rgba(255,255,255,0.9)';
    for (let x = 0; x < w; x += 0.25) g.fillRect(x, 0, 0.02, h);
    g.fillStyle = 'rgba(0,0,0,0.75)';
    for (let y = 0; y < h; y += 0.25) g.fillRect(0, y, w, 0.02);
    g.fillStyle = '#ffffff';
    g.font = 'bold 0.3px sans-serif';
    g.textBaseline = 'top';
    g.fillText(label, 0.08, 0.06);
    g.fillStyle = '#ffe14a';
    g.beginPath();
    g.arc(w / 2, h / 2, 0.15, 0, Math.PI * 2);
    g.fill();
  };
  grid(v.side, L, H, '#b3202a', 'SIDE nose>');
  grid(v.top, L, W, '#1f8a4c', 'TOP nose>');
  grid(v.front, W, H, '#2552c8', 'FRONT');
  grid(v.rear, W, H, '#7a2fc0', 'REAR');
};

// ---------------------------------------------------------------------------
// Geometry every kit shares.
// ---------------------------------------------------------------------------

interface Geo {
  fit: BodyFit;
  /** Side-view y of the belt line (the base of the glass). */
  beltY: number;
  /** Where the front panel's collar line sits (the base of the windscreen)
   *  and where the back panel's sits (the base of the rear glass). */
  cowl: number;
  tailGlass: number;
  /** Where the shirt's back panel starts (behind all the cabin glass). */
  deckX: number;
  /** True when the ride has a real cabin (collar ring + hood neck apply). */
  cabin: boolean;
  /** Wheel arches worth a cuff: enclosed arches only. */
  arches: { x: number; y: number; r: number }[];
  /** The doors: the flank between the arches (the billboard's body). */
  door0: number;
  door1: number;
}

function geoOf(fit: BodyFit): Geo {
  const beltY = fit.H * (1 - fit.belt);
  // (Round 2: dressing the Formula's open cockpit like a cabin broke every
  // kit built on one big graphic, the sash, Ajax's band, France 98's band,
  // so its shirt keeps the no-cabin dress; only the collar uses the cockpit.)
  const room = fit.cabin;
  const cabin = !!room;
  // Arches worth a cuff: wheels the body's flank wraps round (paint just
  // above the tyre). An open wheel (the Formula's) has none.
  const arches = cabin
    ? fit.wheels.filter(
        (w) =>
          w.y > fit.H - 1.7 * w.r &&
          w.y < fit.H + 0.7 * w.r &&
          (!fit.flank || flankCover(fit, w.x - 0.08, w.x + 0.08, w.y - 1.3 * w.r, w.y - 1.15 * w.r) >= 0.5),
      )
    : [];
  const first = arches[0];
  const last = arches[arches.length - 1];
  const span = billboardSpan(fit, beltY, room);
  let door0 = span ? span[0] : first ? first.x + first.r * 1.3 : fit.L * 0.24;
  let door1 = span ? span[1] : last && arches.length > 1 ? last.x - last.r * 1.3 : fit.L * 0.78;
  if (fit.cabin && fit.plan) {
    // The doors start just behind the screen pillar's foot, not over the
    // front arch (the pickup's chest band ran 0.3-0.5 m onto the fender).
    door0 = Math.max(door0, fit.cabin.x0 + 0.12);
    // An OPEN BED behind the cab (the pickup's bed floor is trim, so the
    // plan map is empty there): the shirt's doors end at the cab, never
    // across the cab/bed slot onto the bed side (Boca's stars straddled it).
    if (planCover(fit, fit.cabin.x1 + 0.25, fit.L - 0.25, fit.W * 0.3, fit.W * 0.7) < 0.35) {
      door1 = Math.min(door1, fit.cabin.x1 + 0.06);
    }
    if (door1 - door0 < fit.L * 0.15) {
      door0 = span ? span[0] : door0;
      door1 = span ? span[1] : door1;
    }
  }
  const tailGlass = room?.x1 ?? fit.L * 0.72;
  return {
    fit,
    beltY,
    cowl: room?.x0 ?? fit.L * 0.3,
    tailGlass,
    // M153: the back panel starts behind ALL the cabin's glass (the coupe's
    // fastback window): from the side windows' end, Boca's back band slid up
    // under the coupe's wing and the tail panel went plain (round 2).
    deckX: Math.max(tailGlass, fit.glassEnd ?? tailGlass),
    cabin,
    arches,
    door0,
    door1,
  };
}

/** M153: where a wheel arch's EDGE really is on this body: the smallest
 *  radius at which the flank map is metal round the arch's top. The cuff's
 *  hem sits there (a fixed 1.14 x the tyre radius landed inside the muscle's
 *  wide arches and left stripe shards between the ring and the lip). */
function archEdge(fit: BodyFit, a: { x: number; y: number; r: number }): number {
  if (!fit.flank) return a.r * 1.14;
  for (let k = 1.0; k <= 1.8; k += 0.02) {
    const R = a.r * k;
    let on = 0;
    let n = 0;
    for (let t = 0.15; t <= 0.851; t += 0.07) {
      const ang = Math.PI * (1 + t);
      const x = a.x + Math.cos(ang) * R;
      const y = a.y + Math.sin(ang) * R;
      if (y < 0 || x < 0 || x > fit.L) continue;
      n++;
      if (flankCover(fit, x - 0.015, x + 0.015, y - 0.015, y + 0.015) >= 0.5) on++;
    }
    if (n > 0 && on / n >= 0.75) return R;
  }
  return a.r * 1.14;
}

/** M153: the top of the metal under the side glass (or round an open
 *  cockpit): the first row of the flank map, scanning down from just above
 *  the belt, that is metal along the doors. The collar ring sits there (at
 *  the measured belt it landed a hand below the coupe's side glass and off
 *  the muscle car's cockpit rim). */
function capY(geo: Geo): number {
  const fit = geo.fit;
  if (!fit.flank || !geo.cabin) return geo.beltY;
  const step = fit.H / fit.flank.rows;
  const x0 = geo.cowl + 0.1;
  const x1 = Math.max(x0 + 0.1, geo.tailGlass - 0.1);
  for (let y = Math.max(0, geo.beltY - 0.15); y <= geo.beltY + 0.3; y += step) {
    if (flankCover(fit, x0, x1, y, y + step * 0.5) >= 0.55) return y;
  }
  return geo.beltY;
}

/** M153: the hood's half-width at `x` (metres from the nose), from the plan
 *  map: how far out from the centre line the OUTERMOST upward-facing paint
 *  reaches (holes inside it, the sports car's cowl vents, do not narrow it:
 *  stopping at the first one squeezed every neck between the vents, round
 *  2). The full body width on the generic fit. */
function halfWidthAt(fit: BodyFit, x: number): number {
  if (!fit.plan) return fit.W / 2;
  const c = fit.W / 2;
  const step = fit.W / fit.plan.rows;
  for (let d = c - step / 2; d > step; d -= step) {
    const ok =
      planCover(fit, x - 0.06, x + 0.06, c + d - step / 2, c + d + step / 2) >= 0.5 ||
      planCover(fit, x - 0.06, x + 0.06, c - d - step / 2, c - d + step / 2) >= 0.5;
    if (ok) return d;
  }
  return step;
}

/** M153: is a hood point (panel space: v metres toward the nose from the
 *  cowl, u metres across from the centre) on upward-facing paint? */
function hoodClear(geo: Geo, pv: number, pu: number): boolean {
  const f = geo.fit;
  const x = geo.cowl - pv;
  const z = f.W / 2 + pu;
  return planCover(f, x - 0.03, x + 0.03, z - 0.03, z + 0.03) >= 0.5;
}

/** Where a neck's cut starts on the hood (metres ahead of the cowl). */
const NECK_V0 = 0.03;

/**
 * M153 round 4 — where the neck goes on THIS hood. Every point of its
 * outline (the arms, the tip, a collar's leaves) must land on paint with a
 * margin: a neck that ran on across a vent or a dish was chopped into letters
 * (F V F between the sports car's cowl vents, Z / M / W round the coupe's
 * dishes, a K over the monster truck's vent), round after round while the
 * rule only asked for MOST of it on paint. A shallower V tucked between the
 * holes still read as a letter with them (an M between the coupe's dishes, a
 * house between the sports car's vents), so a cut that does not fit whole
 * becomes the clean shallow ARC that read well in every round (the crew
 * necks), as deep as the hood allows, moved ahead of vents at the cowl when
 * it must. The first few centimetres at the glass are not tested (the top
 * map there is the windscreen's edge).
 */
function fitNeck(
  geo: Geo,
  depth: number,
  v0: number,
  path: (d: number, o: number) => [number, number][],
  reach: number,
  forceArc = false,
): { depth: number; v0: number; arc: boolean } {
  if (!geo.fit.plan) return forceArc ? { depth: Math.min(0.22, depth * 0.6), v0, arc: true } : { depth, v0, arc: false };
  const f = geo.fit;
  const okAt = (pv: number, pu: number): boolean => {
    const x = geo.cowl - pv;
    const z = f.W / 2 + pu;
    return planCover(f, x - 0.07, x + 0.07, z - 0.06, z + 0.06) >= 0.85;
  };
  const clear = (pts: [number, number][], o: number): boolean =>
    pts.every(([pv, pu]) => (o === v0 && pv < v0 + 0.06) || okAt(pv, pu));
  if (!forceArc && clear(path(depth, v0), v0)) return { depth, v0, arc: false };
  const arc = (d: number, o: number): [number, number][] => {
    const out: [number, number][] = [];
    for (let i = 0; i <= 12; i++) {
      const a = (i / 12) * Math.PI;
      out.push([o + Math.sin(a) * d, -Math.cos(a) * reach]);
    }
    return out;
  };
  for (let d = Math.min(0.22, depth * 0.6); d >= 0.05; d *= 0.85) if (clear(arc(d, v0), v0)) return { depth: d, v0, arc: true };
  for (let sh = 0.03; sh <= 0.25 + 1e-9; sh += 0.03) {
    for (const d of [0.14, 0.1, 0.07]) if (clear(arc(d, v0 + sh), v0 + sh)) return { depth: d, v0: v0 + sh, arc: true };
  }
  return { depth: 0.03, v0, arc: true };
}

/**
 * M153 — the shirt BILLBOARD on this body: the longest stretch of the flank
 * that is metal from just under the glass to above the sill (the flank map).
 * It ends before a side scoop (the sports car, the monster truck), a gill
 * slit (the coupe) or an arch, and on the bike it is the fairing, not the
 * engine window. Null on the generic body (the arch rule then stands).
 */
function billboardSpan(fit: BodyFit, beltY: number, room?: { x0: number; x1: number }): [number, number] | null {
  const f = fit.flank;
  if (!f) return null;
  const flank = fit.H - beltY;
  const y0 = beltY + flank * 0.1;
  const y1 = fit.H - flank * 0.25;
  const step = fit.L / f.cols;
  const runs: [number, number][] = [];
  let run0 = -1;
  for (let c = 0; c <= f.cols; c++) {
    const ok = c < f.cols && flankCover(fit, c * step, (c + 0.999) * step, y0, y1) >= 0.72;
    if (ok && run0 < 0) run0 = c;
    if (!ok && run0 >= 0) {
      if ((c - run0) * step >= fit.L * 0.18) runs.push([run0 * step, c * step]);
      run0 = -1;
    }
  }
  if (runs.length === 0) return null;
  // The Formula: its flank is a nose run and a sidepod run either side of
  // the pod inlet; the shirt spans both (a band, the sash) and simply
  // crosses the inlet, as a stripe crosses a vent (round 2).
  if (fit.cockpit && runs.length > 1) return [runs[0][0] + 0.04, runs[runs.length - 1][1] - 0.04];
  // M153: with a cabin the billboard is the run that lies under it and just
  // behind it (the doors), not simply the longest.
  const score = (r: [number, number]): number =>
    room ? Math.max(0, Math.min(r[1], room.x1 + 1.0) - Math.max(r[0], room.x0 - 0.3)) + 0.01 * (r[1] - r[0]) : r[1] - r[0];
  const best = runs.reduce((a, b) => (score(b) > score(a) ? b : a));
  return [best[0] + 0.04, best[1] - 0.04];
}

function fill(g: G, color: number): void {
  g.fillStyle = css(color);
  g.fillRect(-BIG, -BIG, 2 * BIG, 2 * BIG);
}

function allViews(v: LiveryViews): G[] {
  return [v.side, v.top, v.front, v.rear];
}

/** Bands of `color` across one axis: centres at `origin + k·period`. */
function bands(g: G, axis: 'x' | 'y', origin: number, period: number, width: number, color: number | string): void {
  g.fillStyle = typeof color === 'number' ? css(color) : color;
  const k0 = Math.floor((-BIG - origin) / period);
  const k1 = Math.ceil((BIG - origin) / period);
  for (let k = k0; k <= k1; k++) {
    const c = origin + k * period;
    if (axis === 'x') g.fillRect(c - width / 2, -BIG, width, 2 * BIG);
    else g.fillRect(-BIG, c - width / 2, 2 * BIG, width);
  }
}

/**
 * PANEL SPACE: (v, u) = (metres down the shirt from the collar line, metres
 * across from the centre). `inPanel` runs `draw` with the context mapped so
 * that panel x = v and panel y = u, for the four places a panel lies:
 *   hood  the front panel, running from the windscreen toward the nose
 *   nose  the front panel, carrying on down the nose face
 *   deck  the back panel, running from the rear glass toward the tail (and
 *         forward over the roof, where v is negative)
 *   tail  the back panel, carrying on down the tail face
 */
type PanelPlace = 'hood' | 'nose' | 'deck' | 'tail';

function inPanel(v: LiveryViews, geo: Geo, place: PanelPlace, draw: (g: G) => void): void {
  const { L, W } = geo.fit;
  const g = place === 'hood' || place === 'deck' ? v.top : place === 'nose' ? v.front : v.rear;
  g.save();
  if (place === 'hood') {
    // The hood only: the roof and deck belong to the back panel.
    g.beginPath();
    g.rect(-BIG, -BIG, BIG + geo.cowl + 0.001, 2 * BIG);
    g.clip();
    g.transform(-1, 0, 0, 1, geo.cowl, W / 2);
  } else if (place === 'deck') {
    g.beginPath();
    g.rect(geo.cowl, -BIG, BIG, 2 * BIG);
    g.clip();
    g.transform(1, 0, 0, 1, geo.deckX, W / 2);
  } else if (place === 'nose') {
    // The nose face begins where the hood ends: v = the hood's length there.
    g.transform(0, 1, 1, 0, W / 2, geo.beltY - geo.cowl);
  } else {
    g.transform(0, 1, 1, 0, W / 2, geo.beltY - (L - geo.deckX));
  }
  draw(g);
  g.restore();
}

// ---------------------------------------------------------------------------
// The field.
// ---------------------------------------------------------------------------

function paintField(v: LiveryViews, geo: Geo, spec: KitSpec): void {
  const { L, H, W } = geo.fit;
  const [base, pat] = spec.colors;
  for (const g of allViews(v)) fill(g, base);
  const flank = H - geo.beltY;
  const mid = geo.cabin ? (geo.cowl + geo.tailGlass) / 2 : L / 2;

  switch (spec.pattern) {
    case 'stripes': {
      // One period across the chest → the same fabric everywhere on the car.
      const period = CHEST / (spec.count ?? 5);
      const width = (spec.ratio ?? 0.5) * period;
      const off = (spec.centre ?? 'pattern') === 'pattern' ? 0 : period / 2;
      const lay = (g: G, axis: 'x' | 'y', origin: number): void => {
        bands(g, axis, origin, period, width, pat);
        if (spec.pin !== undefined) {
          // A hairline just inside both edges of every stripe.
          const t = Math.min(0.016, width * 0.09);
          bands(g, axis, origin - width / 2 + t * 2.2, period, t, spec.pin);
          bands(g, axis, origin + width / 2 - t * 2.2, period, t, spec.pin);
        }
      };
      lay(v.top, 'y', W / 2 + off);
      lay(v.front, 'x', W / 2 + off);
      lay(v.rear, 'x', W / 2 + off);
      lay(v.side, 'x', mid + off); // upright on the doors
      break;
    }
    case 'hoops': {
      const topIsPattern = (spec.top ?? 'pattern') === 'pattern';
      // WORN: hoops cross the panels, the first one on the shoulders.
      const period = TORSO / (spec.count ?? 5);
      const width = (spec.ratio ?? 0.5) * period;
      for (const place of ['hood', 'nose', 'deck', 'tail'] as PanelPlace[]) {
        inPanel(v, geo, place, (g) => bands(g, 'x', (topIsPattern ? 0 : period / 2) + width / 2, period, width, pat));
      }
      // SHOWN: the same count of hoops stacked roof → sill on the doors.
      const sp = H / (spec.count ?? 5);
      const sw = (spec.ratio ?? 0.5) * sp;
      bands(v.side, 'y', (topIsPattern ? 0 : sp / 2) + sw / 2, sp, sw, pat);
      break;
    }
    case 'sash': {
      // Shoulder → hip ON THE DOORS (M153: anchored to the billboard, not to
      // fixed shares of the length, which put the pickup's sash on its rear
      // door and bed): it leaves the glass at the front of the door and
      // reaches the sill at the back of it.
      const g = v.side;
      // (On the Formula only along the sidepods: run from the nose, the
      // diagonal sat on the nose's sloping sides and the two flanks crossed
      // as an X seen from above, round 4.)
      const d0 = geo.fit.cockpit ? Math.max(geo.door0, geo.fit.cockpit.x0 - 0.1) : geo.door0;
      const bw = Math.min(0.18 * L, Math.max(0.3, (geo.door1 - d0) * 0.3));
      const xa = d0 + bw * 0.5;
      const xb = Math.max(xa + 0.1, geo.door1 - bw * 0.5);
      const slope = (xb - xa) / Math.max(0.05, H - geo.beltY);
      const xAt = (y: number): number => xa + (y - geo.beltY) * slope;
      g.fillStyle = css(pat);
      g.beginPath();
      g.moveTo(xAt(-0.3) - bw / 2, -0.3);
      g.lineTo(xAt(-0.3) + bw / 2, -0.3);
      g.lineTo(xAt(H + 0.3) + bw / 2, H + 0.3);
      g.lineTo(xAt(H + 0.3) - bw / 2, H + 0.3);
      g.closePath();
      g.fill();
      // Over the top it is the same band, crossing where it leaves the
      // shoulder: across the roof on a car (never clipped away, which left the
      // van's and the sports car's tops plain), over the tank on the bike.
      let xt = xAt(0);
      if (geo.cabin) xt = Math.min(Math.max(xt, geo.cowl + bw * 0.6), geo.tailGlass - bw * 0.6);
      // M153: it crosses where the roof is PAINT. Between the windscreen
      // base and the back of the cabin most of the length is glass on these
      // cars, and the band laid there was invisible (the van's, the sports
      // car's and the coupe's tops read plain white, re-review).
      if (geo.cabin && geo.fit.plan) {
        const f = geo.fit;
        let best = xt;
        let bestScore = -Infinity;
        for (let x = geo.cowl; x <= geo.tailGlass + 0.4; x += 0.04) {
          // ...never far from where the band leaves the shoulder: on the open
          // muscle car the nearest paint was the trunk, 1.7 m back, and the
          // livery read as a Z (round 2). No paint near = no crossing.
          if (Math.abs(x - xt) > 0.8) continue;
          const cover = planCover(f, x - bw / 2, x + bw / 2, f.W * 0.3, f.W * 0.7);
          const sc = cover - 0.25 * Math.abs(x - xt);
          if (cover >= 0.6 && sc > bestScore) {
            bestScore = sc;
            best = x;
          }
        }
        xt = best;
      }
      v.top.fillStyle = css(pat);
      v.top.fillRect(xt - bw / 2, -BIG, bw, 2 * BIG);
      break;
    }
    case 'quarters': {
      // The eight-piece shirt: split down the car's centreline, and swapped
      // front to back. The far flank reads the side painting mirrored.
      v.side.fillStyle = css(pat);
      v.side.fillRect(mid, -BIG, BIG, 2 * BIG);
      v.top.fillStyle = css(pat);
      v.top.fillRect(mid, W / 2, BIG, BIG); // near side, rear
      v.top.fillRect(-BIG, -BIG, BIG + mid, BIG + W / 2); // far side, front
      v.front.fillStyle = css(pat);
      v.front.fillRect(W / 2, -BIG, BIG, 2 * BIG);
      v.rear.fillStyle = css(pat);
      v.rear.fillRect(-BIG, -BIG, BIG + W / 2, 2 * BIG);
      break;
    }
    case 'chestband': {
      const b = spec.band ?? {};
      const lines = b.lines ?? 0;
      const lineColor = b.edge ?? pat;
      // WORN: across the hood at chest height (and the back, unless the
      // shirt carries it on the front only).
      const v0 = (b.y ?? 0.2) * TORSO;
      const h = (b.h ?? 0.2) * TORSO;
      const halfSpan = b.body ? (CHEST * 0.66) / 2 : BIG;
      const worn = (g: G): void => {
        g.fillStyle = css(pat);
        g.fillRect(v0, -halfSpan, h, 2 * halfSpan);
        const t = 0.03;
        if (lines > 0) {
          // Thin lines stepping away below the band (France, 1998).
          for (let i = 1; i <= lines; i++) {
            g.fillStyle = css(lineColor);
            const grow = b.body ? i * 0.03 : 0;
            g.fillRect(v0 + h + i * h * 1.25 + (i - 1) * t, -halfSpan - grow, t, 2 * (halfSpan + grow));
          }
        } else if (b.edge !== undefined) {
          g.fillStyle = css(b.edge);
          g.fillRect(v0 - t * 2.2, -halfSpan, t, 2 * halfSpan);
          g.fillRect(v0 + h + t * 1.2, -halfSpan, t, 2 * halfSpan);
        }
      };
      // M153: on the hood the band slides (a little) to where the hood is
      // paint across its width, off the coupe's dishes and the monster
      // truck's vent (Les Bleus showed only slivers between the dishes).
      const hv0 = (() => {
        if (!geo.fit.plan || !geo.cabin) return v0;
        let best = v0;
        let bestScore = -Infinity;
        const hw = Math.min(halfSpan, halfWidthAt(geo.fit, geo.cowl - v0 - h / 2) * 0.9);
        for (let dv = -0.25; dv <= 0.45 + 1e-9; dv += 0.03) {
          const vv = v0 + dv;
          if (vv < 0.04) continue;
          let on = 0;
          let n = 0;
          for (let a = 0; a <= 1.0001; a += 0.25)
            for (let b = -1; b <= 1.0001; b += 0.25) {
              n++;
              if (hoodClear(geo, vv + a * h, b * hw)) on++;
            }
          const sc = on / n - 0.3 * Math.abs(dv);
          if (sc > bestScore) {
            bestScore = sc;
            best = vv;
          }
        }
        return best;
      })();
      inPanel(v, geo, 'hood', (hg) => {
        hg.save();
        hg.translate(hv0 - v0, 0);
        worn(hg);
        hg.restore();
      });
      inPanel(v, geo, 'nose', worn);
      if (!b.frontOnly) {
        inPanel(v, geo, 'deck', worn);
        inPanel(v, geo, 'tail', worn);
      }
      // SHOWN: along the doors at the same share of the flank.
      const g = v.side;
      const y0 = geo.beltY + (b.y ?? 0.2) * flank * 1.25 + 0.05;
      const sh = Math.max(0.07, (b.h ?? 0.2) * flank * 1.7);
      const x0 = b.body ? geo.door0 : -BIG;
      const x1 = b.body ? geo.door1 : BIG;
      g.fillStyle = css(pat);
      g.fillRect(x0, y0, x1 - x0, sh);
      const t = 0.022;
      if (lines > 0) {
        g.fillStyle = css(lineColor);
        const room = H - (y0 + sh) - 0.04;
        const step = Math.min(sh * 1.25, room / (lines + 0.4));
        // M153: the lines end with the band (they grew past it, and on the
        // pickup three white stubs poked across the cab/bed slot).
        for (let i = 1; i <= lines; i++) g.fillRect(x0, y0 + sh + i * step, x1 - x0, t);
      } else if (b.edge !== undefined) {
        g.fillStyle = css(b.edge);
        g.fillRect(x0, y0 - t * 2.2, x1 - x0, t);
        g.fillRect(x0, y0 + sh + t * 1.2, x1 - x0, t);
      }
      if (spec.stars) {
        // A row of small stars above the band (on the wearer's left chest).
        const st = spec.stars;
        const r = 0.05;
        for (let i = 0; i < st.n; i++) star(g, geo.door1 - 0.16 - i * r * 2.7, y0 - r * 1.7, r, st.color);
        inPanel(v, geo, 'hood', (hg) => {
          for (let i = 0; i < st.n; i++) star(hg, hv0 - r * 1.9, W * 0.16 + i * r * 2.7, r, st.color, Math.PI / 2);
        });
      }
      break;
    }
    case 'centreband': {
      const wd0 = (spec.width ?? 0.42) * CHEST;
      // M153: never wider than 42% of THIS body (the bike's whole tail top
      // turned red under a 0.69 m band).
      const wd = Math.min(wd0, 0.42 * W);
      v.top.fillStyle = css(pat);
      v.top.fillRect(-BIG, W / 2 - wd / 2, 2 * BIG, wd);
      for (const g of [v.front, v.rear]) {
        g.fillStyle = css(pat);
        g.fillRect(W / 2 - wd / 2, -BIG, wd, 2 * BIG);
      }
      // M153 — SHOWN: the band stands upright down the middle of the doors.
      // (It was only ever worn on the top, so from the side Ajax was a plain
      // white car: the billboard rule says the doors show the shirt front.)
      // On the flank it stays a BAND: taller than it is wide (a full 0.69 m
      // on a shallow door read as a square red door panel, re-review).
      const dm = (geo.door0 + geo.door1) / 2;
      // (75% of a tall flank, the pickup's; 60% of a short one: at 75% the
      // muscle car's shallow door wore a square red box, round 3)
      const sw = Math.min(wd0, flank * Math.min(0.75, Math.max(0.6, 0.6 + (flank - 0.5) * 0.75)));
      v.side.fillStyle = css(pat);
      // ...below the windows only: run on up, it painted a stray red patch on
      // the sports car's sail panel under the mirror (round 2).
      v.side.fillRect(dm - sw / 2, geo.beltY - 0.02, sw, 2 * BIG);
      break;
    }
    case 'solid':
    case 'dragon': // painted by livery/dragon, never here
      break;
  }
  if (spec.pattern === 'stripes' || spec.pattern === 'hoops') {
    // M153 — the SHOULDER MARGIN. Where the hood / roof / deck roll over into
    // the flank, the worn panels' lengthwise stripes (or crosswise hoops) meet
    // the billboard's upright stripes (or level hoops). On the rebuilt
    // fleet's haunches and faceted panels that junction tore into shards
    // (measured on every striped kit). A plain margin of the shirt's own
    // colour along both edges of the worn panels, like a jersey's side
    // panel, gives the pattern one clean place to end.
    // M153: the hoops' margin is half as wide: at 12% the worn hoops floated
    // as slabs in a plain frame on the muscle car's wide flat deck.
    // (Round 2: the hoops' narrower 8% margin brought back the dashed strip
    // along the coupe's window line; the muscle car's open deck, which it
    // was for, is handled below.)
    // An OPEN TOP (the muscle convertible) keeps the narrower 8% on its
    // hoops: at 12% its wide flat deck wore the hoops as slabs in a plain
    // frame again (round 3).
    const fit = geo.fit;
    const openTop = !!geo.cabin && !!fit.plan && planCover(fit, geo.cowl + 0.15, geo.tailGlass - 0.15, fit.W * 0.35, fit.W * 0.65) < 0.25;
    const e = (spec.pattern === 'hoops' && openTop ? 0.08 : 0.12) * W;
    v.top.fillStyle = css(base);
    v.top.fillRect(-BIG, -BIG, 2 * BIG, BIG + e);
    v.top.fillRect(-BIG, W - e, 2 * BIG, BIG);
    // An OPEN TOP: the door caps and the deck behind the seats ring the
    // cockpit, and the pattern there broke into dashes along the rims and
    // stubs behind the seats. That ring is plain (the collar sits on it).
    if (openTop) {
      v.top.fillRect(geo.cowl - 0.05, -BIG, geo.tailGlass - geo.cowl + 0.1, 2 * BIG);
    }
  }
}

// ---------------------------------------------------------------------------
// M154 polish r4 — the HAUNCH DRAPE.
// ---------------------------------------------------------------------------

/** A plan cell whose topmost paint faces up this much (its normal's up
 *  component) is FLAT: a deck, a hood, a haunch's crown. */
export const DRAPE_FLAT_NY = 0.85;
/** The flat run the worn pattern lies on must start at least this share of
 *  the width in from the edge before the shoulder outside it is draped
 *  (inside it, the 12% plain shoulder margin already gives the pattern one
 *  clean place to end). */
export const DRAPE_MIN_SHARE = 0.15;
/** ...and over at least this many plan columns in a row (a haunch, about
 *  half a metre: not a fender's corner, nor the old sports car's sail panel,
 *  whose kits read clean live and must not move). */
export const DRAPE_MIN_RUN = 12;

/**
 * (pure; the tests pin it) Per plan column and side (index side·cols + col):
 * how many rows in from the body's edge the WORN pattern's flat run starts,
 * when a HAUNCH lies outside it: a flat crown, then a trough of paint that
 * does not face up, before the run (the rebuilt sports car's rear haunches,
 * either side of the engine cover). 0 = no drape: the column keeps the plain
 * margin. Side 0 counts rows from row 0, side 1 from the last row.
 */
export function drapeZones(plan: FlankMap): Uint8Array {
  const { cols, rows, cov } = plan;
  const ny = plan.ny;
  const out = new Uint8Array(2 * cols);
  if (!ny) return out;
  const half = Math.floor(rows / 2);
  for (let side = 0; side < 2; side++) {
    for (let c = 0; c < cols; c++) {
      const k = (i: number): number => (side === 0 ? i : rows - 1 - i) * cols + c;
      const paint = (i: number): boolean => cov[k(i)] === 1;
      const flat = (i: number): boolean => paint(i) && ny[k(i)]! >= DRAPE_FLAT_NY;
      // the innermost flat run (from the centre line out)
      let i = half - 1;
      while (i >= 0 && !flat(i)) i--;
      if (i < 0) continue;
      while (i > 0 && flat(i - 1)) i--;
      if (i < DRAPE_MIN_SHARE * rows) continue;
      // outside it: a trough (paint, not flat), and a crown further out
      let trough = -1;
      for (let j = i - 1; j >= 0; j--) {
        if (paint(j) && !flat(j)) {
          trough = j;
          break;
        }
        if (!paint(j)) break;
      }
      if (trough < 0) continue;
      let crown = false;
      for (let j = trough - 1; j >= 0; j--) if (flat(j)) crown = true;
      if (crown) out[side * cols + c] = i;
    }
    // a haunch, not a stray column: runs shorter than DRAPE_MIN_RUN go
    for (let c = 0; c < cols; ) {
      if (!out[side * cols + c]) {
        c++;
        continue;
      }
      let e = c;
      while (e < cols && out[side * cols + e]) e++;
      if (e - c < DRAPE_MIN_RUN) for (let q = c; q < e; q++) out[side * cols + q] = 0;
      c = e;
    }
  }
  return out;
}

/** The rides whose kits broke up over a haunch: the rebuilt sports car's
 *  rear haunches either side of its engine cover. No other ride's kit moves.
 *  (The rebuilt pickup's hood reads as a haunch to drapeZones, the scoop's
 *  walls as its trough, but its broad crown IS the hood: a plain panel there
 *  took the hoops off most of the hood. Measured, not taken.) */
export const DRAPE_RIDES: ReadonlySet<string> = new Set(['sports']);
/**
 * M154 polish r5 — the HAUNCH BANDS. Round 4 made the haunch the shirt's
 * plain side panel (white under Celtic's hoops, the haunch the chase camera
 * looks down on: 35% of the live build's green from the angled view). Now
 * the worn panel's bands run on over the haunch to the body's edge, ACROSS
 * the car, in the phase the haunch's own outer face already shows them:
 *   stripes  the billboard's upright stripes (one phase along the car) carry
 *            on over the haunch's crown as crosswise bars, so where the
 *            shader blends the side and top paintings on the haunch's
 *            shoulder both say the same thing;
 *   hoops    the deck's crosswise hoops run out over the haunch to the edge
 *            (the M153 shoulder margin is dropped there): the flank's level
 *            hoops end along the haunch's shoulder, as they end under a
 *            car's deck.
 * Only the TOP painting changes, only over the haunch (drapeZones), only on
 * the DRAPE_RIDES.
 */
export function haunchBandPhase(
  spec: Pick<KitSpec, 'pattern' | 'count' | 'ratio' | 'centre' | 'top'>,
  geo: { cowl: number; tailGlass: number; deckX: number; cabin: boolean },
  L: number,
): { origin: number; period: number; width: number } | null {
  if (spec.pattern === 'stripes') {
    const period = CHEST / (spec.count ?? 5);
    const off = (spec.centre ?? 'pattern') === 'pattern' ? 0 : period / 2;
    const mid = geo.cabin ? (geo.cowl + geo.tailGlass) / 2 : L / 2;
    return { origin: mid + off, period, width: (spec.ratio ?? 0.5) * period };
  }
  if (spec.pattern === 'hoops') {
    const period = TORSO / (spec.count ?? 5);
    const width = (spec.ratio ?? 0.5) * period;
    const topIsPattern = (spec.top ?? 'pattern') === 'pattern';
    return { origin: geo.deckX + (topIsPattern ? 0 : period / 2) + width / 2, period, width };
  }
  return null;
}

/**
 * (pure; the tests pin it) Per plan column and side (drapeZones' layout):
 * over the cabin (x0..x1 metres along the car), how many rows in from the
 * body's edge the paint runs before it meets the cabin's GLASS (a gap in
 * the plan map), when that strip is a narrow shoulder (2 rows to 30% of the
 * width); 0 elsewhere (a painted roof, no glass, or no shoulder).
 */
export function glassShoulders(plan: FlankMap, x0: number, x1: number, L: number): Uint8Array {
  const { cols, rows, cov } = plan;
  const out = new Uint8Array(2 * cols);
  for (let side = 0; side < 2; side++) {
    for (let c = 0; c < cols; c++) {
      const xc = ((c + 0.5) / cols) * L;
      if (xc < x0 || xc > x1) continue;
      const k = (i: number): number => (side === 0 ? i : rows - 1 - i) * cols + c;
      let i = 0;
      while (i < rows && !cov[k(i)]) i++;
      const from = i;
      while (i < rows / 2 && cov[k(i)]) i++;
      if (i >= rows / 2) continue;
      if (i - from >= 2 && i <= 0.3 * rows) out[side * cols + c] = i;
    }
  }
  return out;
}

function bandedHaunches(v: LiveryViews, geo: Geo, spec: KitSpec): void {
  const fit = v.fit;
  const plan = fit.plan;
  if (!DRAPE_RIDES.has(fit.vehicle) || !plan?.height) return;
  const phase = haunchBandPhase(spec, geo, fit.L);
  if (!phase) return;
  const zones = drapeZones(plan);
  if (!zones.some((z) => z > 0)) return;
  const { cols, rows } = plan;
  const { L, W } = fit;
  // ...and forward of the haunch, the shoulders beside the cabin's glass
  // (from the edge in to the glass): the same bands carry on along them to
  // the windscreen, as the chase camera sees them either side of the canopy
  const shoulders = glassShoulders(plan, geo.cowl, geo.deckX, L);
  for (let k = 0; k < zones.length; k++) if (!zones[k] && shoulders[k]) zones[k] = shoulders[k]!;
  const [base, pat] = spec.colors;
  // the TOP painting's y runs from the body's far side (z = W at y = 0), so
  // side 0, counted from z = 0, is the canvas's bottom
  const g = v.top;
  g.save();
  g.beginPath();
  for (let side = 0; side < 2; side++) {
    for (let c = 0; c < cols; c++) {
      const m = zones[side * cols + c]!;
      if (!m) continue;
      const d = (m / rows) * W;
      const x0 = (c / cols) * L;
      const x1 = ((c + 1) / cols) * L;
      // (overlapping a hair: abutting anti-aliased edges let the plain
      // margin under them show through as faint dashes along the haunch)
      g.rect(x0 - 0.004, side === 0 ? W - d : -BIG, x1 - x0 + 0.008, side === 0 ? BIG : BIG + d);
    }
  }
  g.clip();
  g.fillStyle = css(base);
  g.fillRect(-BIG, -BIG, 2 * BIG, 2 * BIG);
  bands(g, 'x', phase.origin, phase.period, phase.width, pat);
  if (spec.pattern === 'stripes' && spec.pin !== undefined) {
    const t = Math.min(0.016, phase.width * 0.09);
    bands(g, 'x', phase.origin - phase.width / 2 + t * 2.2, phase.period, t, spec.pin);
    bands(g, 'x', phase.origin + phase.width / 2 - t * 2.2, phase.period, t, spec.pin);
  }
  g.restore();
}

function star(g: G, cx: number, cy: number, r: number, color: number, rot = 0): void {
  g.fillStyle = css(color);
  g.beginPath();
  for (let i = 0; i < 10; i++) {
    const rad = i % 2 === 0 ? r : r * 0.42;
    const a = rot - Math.PI / 2 + (i * Math.PI) / 5;
    const x = cx + Math.cos(a) * rad;
    const y = cy + Math.sin(a) * rad;
    if (i === 0) g.moveTo(x, y);
    else g.lineTo(x, y);
  }
  g.closePath();
  g.fill();
}

// ---------------------------------------------------------------------------
// The yoke (England, 1980): bands across the shoulders, front only.
// ---------------------------------------------------------------------------

function paintYoke(v: LiveryViews, geo: Geo, spec: KitSpec): void {
  const yoke = spec.yoke;
  if (!yoke) return;
  const total = yoke.weights.reduce((a, b) => a + b, 0);
  const lay = (g: G, axis: 'x' | 'y', from: number, depth: number): void => {
    let at = from;
    yoke.colors.forEach((c, i) => {
      const t = (yoke.weights[i] / total) * depth;
      if (c !== null) {
        g.fillStyle = css(c);
        if (axis === 'x') g.fillRect(at, -BIG, t, 2 * BIG);
        else g.fillRect(-BIG, at, 2 * BIG, t);
      }
      at += t;
    });
  };
  // WORN: the block lies across the hood under the windscreen and ends a
  // quarter of the way down the shirt, where the point of the V lands.
  inPanel(v, geo, 'hood', (g) => lay(g, 'x', 0, TORSO * 0.25));
  // SHOWN: the same block as a pack of lines under the glass, door to door.
  lay(v.side, 'y', geo.beltY + COLLAR_T + 0.045, Math.max(0.17, (geo.fit.H - geo.beltY) * 0.34));
}

// ---------------------------------------------------------------------------
// Tonal weaves.
// ---------------------------------------------------------------------------

/**
 * The Netherlands' 1988 print, as it was cut: each half of the shirt is
 * filled with 45° bands rising toward the centre line (so the middle reads as
 * a stack of arrows pointing up at the collar). The bands alternate between
 * two cuts — one sliced by vertical lines into parallelograms with upright
 * sides, one sliced by level lines into parallelograms with flat tops — and
 * every facet fades from solid orange along its top edge to almost white
 * along its bottom edge, inside a grey hairline.
 *
 * Drawn in PANEL space (x = v down the shirt, y = u across).
 */
export function oranje88(g: G, base: number, vMin: number, vMax: number, uMax: number): void {
  const pale = mixColor(base, 0xffffff, 0.7);
  const band = CHEST / 4; // one arrow per quarter chest
  const cut = CHEST / 8;
  const hair = rgba(0x8c8c8c, 0.6);
  g.lineWidth = 0.006;
  g.lineJoin = 'miter';
  for (const s of [-1, 1]) {
    // Facets on one half: u' = s·u ≥ 0. Band index from c = v − u'.
    const c0 = Math.floor((vMin - uMax) / band) - 1;
    const c1 = Math.ceil(vMax / band) + 1;
    for (let k = c0; k <= c1; k++) {
      const cTop = k * band;
      const upright = ((k % 2) + 2) % 2 === 0;
      if (upright) {
        // Sliced by lines of constant u': parallelograms with upright sides.
        for (let u0 = 0; u0 < uMax; u0 += cut) {
          const u1 = u0 + cut;
          const quad: Pt[] = [
            [cTop + u0, s * u0],
            [cTop + u1, s * u1],
            [cTop + band + u1, s * u1],
            [cTop + band + u0, s * u0],
          ];
          facet(g, quad, [cTop + (u0 + u1) / 2, s * (u0 + u1) / 2], [cTop + band + (u0 + u1) / 2, s * (u0 + u1) / 2], base, pale, hair);
        }
      } else {
        // Sliced by lines of constant v: parallelograms with flat tops.
        for (let v0 = Math.floor((cTop - 0.001) / cut) * cut; v0 < cTop + band + uMax; v0 += cut) {
          const v1 = v0 + cut;
          // Inside the band: cTop ≤ v − u' ≤ cTop + band.
          const quad: Pt[] = [
            [v0, s * Math.max(0, v0 - cTop - band)],
            [v0, s * Math.max(0, v0 - cTop)],
            [v1, s * Math.max(0, v1 - cTop)],
            [v1, s * Math.max(0, v1 - cTop - band)],
          ];
          if (quad[1][1] === quad[0][1] && quad[2][1] === quad[3][1]) continue;
          if (Math.abs(quad[0][1]) > uMax && Math.abs(quad[3][1]) > uMax) continue;
          facet(g, quad, [v0, 0], [v1, 0], base, pale, hair);
        }
      }
    }
  }
}

function facet(g: G, quad: Pt[], from: Pt, to: Pt, solid: number, pale: number, hair: string): void {
  // Solid along the top edge, holding its colour through the facet and only
  // breaking up into white toward the bottom edge (the print's speckle fade).
  const grad = g.createLinearGradient(from[0], from[1], to[0], to[1]);
  grad.addColorStop(0, css(solid));
  grad.addColorStop(0.5, css(mixColor(solid, pale, 0.12)));
  grad.addColorStop(0.82, css(mixColor(solid, pale, 0.5)));
  grad.addColorStop(1, css(pale));
  g.fillStyle = grad;
  g.beginPath();
  quad.forEach(([x, y], i) => (i === 0 ? g.moveTo(x, y) : g.lineTo(x, y)));
  g.closePath();
  g.fill();
  g.strokeStyle = hair;
  g.stroke();
}

/** Tonal weave over the whole shirt: same-hue lights and darks, a few %. */
function paintShadow(v: LiveryViews, geo: Geo, spec: KitSpec): void {
  if (!spec.shadow) return;
  const { L, W, H } = geo.fit;
  const pale = luma(spec.colors[0]) > 0.7;
  const dark = rgba(0x000000, pale ? 0.07 : 0.11);
  const light = rgba(0xffffff, pale ? 0.5 : 0.09);
  if (spec.shadow === 'oranje') {
    const base = spec.colors[0];
    inPanel(v, geo, 'hood', (g) => oranje88(g, base, -0.3, geo.cowl + 0.3, W));
    inPanel(v, geo, 'nose', (g) => oranje88(g, base, geo.cowl - 0.6, geo.cowl + H + 0.3, W));
    inPanel(v, geo, 'deck', (g) => oranje88(g, base, -(geo.tailGlass - geo.cowl) - 0.3, L - geo.tailGlass + 0.3, W));
    inPanel(v, geo, 'tail', (g) => oranje88(g, base, L - geo.tailGlass - 0.6, L - geo.tailGlass + H + 0.3, W));
    // SHOWN on the doors: the shirt front, its arrows climbing to the glass.
    const g = v.side;
    g.save();
    const mid = geo.cabin ? (geo.cowl + geo.tailGlass) / 2 : L / 2;
    g.transform(0, 1, 1, 0, mid, 0);
    oranje88(g, base, -0.4, H + 0.3, L);
    g.restore();
    return;
  }
  for (const g of allViews(v)) {
    const top = g === v.top;
    if (spec.shadow === 'pinstripe') {
      // Fine vertical shadow stripes, about 4 cm apart on the real cloth.
      bands(g, top ? 'y' : 'x', top ? W / 2 : 0, 0.11, 0.02, dark);
    } else if (spec.shadow === 'hoops') {
      bands(g, top ? 'x' : 'y', 0, 0.12, 0.05, dark);
    } else if (spec.shadow === 'diamonds') {
      const s = 0.2;
      for (let r = -2; r < 30; r++) {
        for (let c = -2; c < 60; c++) {
          const cx = (c + (r % 2 ? 0.5 : 0)) * s;
          const cy = r * s * 0.5;
          g.fillStyle = light;
          g.beginPath();
          g.moveTo(cx, cy - s / 2);
          g.lineTo(cx + s / 2, cy);
          g.lineTo(cx, cy + s / 2);
          g.lineTo(cx - s / 2, cy);
          g.closePath();
          g.fill();
          g.fillStyle = dark;
          g.beginPath();
          g.moveTo(cx + s / 2, cy);
          g.lineTo(cx, cy + s / 2);
          g.lineTo(cx - s / 2, cy);
          g.closePath();
          g.fill();
        }
      }
    }
  }
}

// ---------------------------------------------------------------------------
// Trim: collar (the beltline ring + the neck cut into the hood), cuffs (the
// wheel arches), raglan piping.
// ---------------------------------------------------------------------------

const COLLAR_T = 0.085; // collar rib thickness, metres
type Band = [number, number];

/**
 * A rib `t` thick along a polyline, built from bands. `pts` is the rib's
 * INNER edge; the bands stack away from it along `out` (a unit direction),
 * so a tricolour collar is three parallel strokes, never a symmetric
 * sandwich.
 */
function rib(g: G, pts: Pt[], out: Pt, t: number, bandsIn: Band[]): void {
  const total = bandsIn.reduce((a, b) => a + b[1], 0);
  let at = 0;
  g.lineJoin = 'miter';
  g.lineCap = 'butt';
  for (const [color, weight] of bandsIn) {
    const w = (weight / total) * t;
    const o = at + w / 2;
    g.strokeStyle = css(color);
    // A hair of overlap so neighbouring bands never show the paint between.
    g.lineWidth = w + 0.004;
    g.beginPath();
    pts.forEach(([x, y], i) => (i === 0 ? g.moveTo(x + out[0] * o, y + out[1] * o) : g.lineTo(x + out[0] * o, y + out[1] * o)));
    g.stroke();
    at += w;
  }
}

const collarBandsOf = (spec: KitSpec, collar: number): Band[] => spec.collarBands ?? [[collar, 1]];

function paintCollar(v: LiveryViews, geo: Geo, spec: KitSpec): void {
  const collar = spec.collar === false ? undefined : (spec.collar ?? spec.accent);
  if (collar === undefined) return;
  const { L, W } = geo.fit;
  const t = COLLAR_T;
  const neck = spec.neck ?? 'crew';
  const bandsC = collarBandsOf(spec, collar);

  if (!geo.cabin) {
    // The Formula: one band across the nose just ahead of the cockpit, on
    // the top only (round 1 had it round the nose tip; a ring round the
    // cockpit ran down the body side as an upright stripe, round 2).
    if (geo.fit.cockpit) {
      const xc = geo.fit.cockpit.x0 - 0.06;
      rib(v.top, [[xc, -BIG], [xc, BIG]], [-1, 0], t * 1.3, bandsC);
      return;
    }
    // No cabin (the bike): the collar is a band round the nose, its inner
    // edge toward the tail.
    const x0 = 0.09 * L;
    for (const g of [v.side, v.top]) rib(g, [[x0, -BIG], [x0, BIG]], [-1, 0], t * 1.3, bandsC);
    return;
  }

  // The ring: under the side glass (inner edge = the glass), on the top of
  // the metal the flank map finds there (M153)…
  const ringY = capY(geo);
  // (Not on a sash shirt: the ring met the top of the sash under the glass
  // and the door read as a red cross, round 4.)
  if (spec.pattern !== 'sash') rib(v.side, [[geo.cowl - 0.04, ringY + 0.02], [geo.tailGlass + 0.04, ringY + 0.02]], [0, 1], t, bandsC);
  // …and round the back of the neck, across the deck behind the rear glass.
  // M153: on a patterned shirt (stripes, hoops, halves, quarters, a centre
  // band) that rib cut the roof or deck into a plaid the chase camera sees
  // first (round 2, coupe + sports); there the back of the neck is left to
  // the pattern.
  const patterned = ['stripes', 'hoops', 'half', 'quarters', 'centreband'].includes(spec.pattern);
  // (Only across a deck that is paint from side to side: behind the coupe's
  // fastback the rib landed on the falling tail, and the top view drew it
  // as two short slashes on the rear haunches, round 3.)
  const deckPaint = !geo.fit.plan || planCover(geo.fit, geo.deckX + 0.01, geo.deckX + 0.03 + t, W * 0.15, W * 0.85) >= 0.7;
  if (!patterned && deckPaint) inPanel(v, geo, 'deck', (g) => rib(g, [[0.02, -BIG], [0.02, BIG]], [1, 0], t, bandsC));

  // The neck itself, cut into the hood. Panel space: x = v toward the nose.
  // Round 5: an open or polo collar, and any collar over stripes, hoops,
  // quarters or halves, is drawn as the ARC on every ride: from the chase
  // camera the collar's leaves read as an M and a V over a pattern as an X,
  // K or N (the pickup, whose hood had room for the full cut). The shop's
  // shirt card keeps the real collar.
  // (England's shoulder yoke too: the V crossed its bands into an X.)
  const forceArc = neck === 'open' || neck === 'polo' || !!spec.yoke || ['stripes', 'hoops', 'quarters', 'half'].includes(spec.pattern);
  inPanel(v, geo, 'hood', (g) => {
    // M153: the hood's own half-width at the screen (the Formula's nose is a
    // third of its sidepods' width), from the plan map.
    const half = Math.min(W / 2, halfWidthAt(geo.fit, geo.cowl - 0.12) + 0.02);
    const reach = 0.84 * half; // the collar points land at the screen pillars
    const hood = geo.cowl;
    if (neck === 'vee' || neck === 'wrap') {
      // M153: fitted so its arms and tip stay on paint (fitNeck).
      const veePath = (d: number, o: number): [number, number][] =>
        [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1].flatMap((k): [number, number][] => [
          [o + d * k, -reach * (1 - k)],
          [o + d * k, reach * (1 - k)],
        ]);
      const nf = fitNeck(geo, Math.min(hood * 0.46, 0.6), NECK_V0, veePath, reach, forceArc);
      const { depth, v0 } = nf;
      const arcPts: Pt[] = [];
      for (let i = 0; i <= 28; i++) {
        const a = (i / 28) * Math.PI;
        arcPts.push([nf.v0 + Math.sin(a) * nf.depth, -Math.cos(a) * reach]);
      }
      if (nf.arc) {
        if (spec.collarHalves) {
          rib(g, arcPts.slice(0, 15), [1, 0], t * 1.25, [[spec.collarHalves[1], 1]]);
          rib(g, arcPts.slice(14), [1, 0], t * 1.25, [[spec.collarHalves[0], 1]]);
        } else rib(g, arcPts, [1, 0], t * 1.25, bandsC);
      } else if (spec.collarHalves) {
        // The eight-piece collar: each arm in the other half's colour.
        // M153: the arm on each half wears the OTHER half's colour (they
        // were drawn on the wrong sides, so each arm vanished into its half).
        rib(g, [[v0, -reach], [v0 + depth, 0]], [1, 0], t * 1.25, [[spec.collarHalves[1], 1]]);
        rib(g, [[v0 + depth, 0], [v0, reach]], [1, 0], t * 1.25, [[spec.collarHalves[0], 1]]);
      } else if (neck === 'wrap') {
        // Wrap-over: one side laid across the other at the point.
        rib(g, [[v0, -reach], [v0 + depth * 1.12, reach * 0.14]], [1, 0], t * 1.25, bandsC);
        rib(g, [[v0, reach], [v0 + depth, -reach * 0.02]], [1, 0], t * 1.25, bandsC);
      } else {
        rib(g, [[v0, -reach], [v0 + depth, 0], [v0, reach]], [1, 0], t * 1.25, bandsC);
      }
    } else if (neck === 'polo' || neck === 'open') {
      // Two collar leaves folded back from the centre. A POLO has a buttoned
      // placket below them; an OPEN collar sits over a plain V (no buttons).
      // Fitted like the V: the leaves' free edges and the V under them stay
      // on paint.
      const openPath = (d: number, o: number): [number, number][] =>
        [0, 0.15, 0.3, 0.45, 0.6, 0.75, 0.9, 1].flatMap((k): [number, number][] => [
          [o + d * 1.3 * k, -reach * 0.42 * (1 - k)],
          [o + d * 1.3 * k, reach * 0.42 * (1 - k)],
          [o - 0.06 + (d + 0.06) * k, -reach * (1 - 0.54 * k)],
          [o - 0.06 + (d + 0.06) * k, reach * (1 - 0.54 * k)],
        ]);
      const nf = fitNeck(geo, Math.min(hood * 0.36, 0.44), NECK_V0, openPath, reach, forceArc);
      const { depth, v0 } = nf;
      const tipV = v0 + depth;
      const arcPts: Pt[] = [];
      for (let i = 0; i <= 28; i++) {
        const a = (i / 28) * Math.PI;
        arcPts.push([nf.v0 + Math.sin(a) * nf.depth, -Math.cos(a) * reach]);
      }
      if (nf.arc) {
        rib(g, arcPts, [1, 0], t * 1.25, bandsC);
        if (neck === 'polo') {
          // the buttoned placket hangs from the middle of the arc
          const placket = Math.min(hood * 0.22, 0.26);
          const pv = v0 + depth + 0.02;
          g.fillStyle = css(spec.placket ?? mixColor(spec.colors[0], 0x000000, 0.14));
          g.fillRect(pv, -0.045, placket, 0.09);
          g.fillStyle = css(luma(spec.placket ?? spec.colors[0]) > 0.6 ? 0x1b1d24 : 0xf2f4f8);
          for (const k of spec.buttons === 3 ? [0.26, 0.54, 0.82] : [0.36, 0.76]) {
            g.beginPath();
            g.arc(pv + placket * k, 0, 0.021, 0, Math.PI * 2);
            g.fill();
          }
        }
        return;
      }
      if (neck === 'open') {
        const vee = spec.vneck ?? mixColor(spec.colors[0], 0x000000, 0.2);
        rib(g, [[v0, -reach * 0.42], [v0 + depth * 1.3, 0], [v0, reach * 0.42]], [1, 0], t * 0.9, [[vee, 1]]);
      } else {
        const placket = Math.min(hood * 0.3, 0.36);
        const pv = v0 + depth * 0.3;
        g.fillStyle = css(spec.placket ?? mixColor(spec.colors[0], 0x000000, 0.14));
        g.fillRect(pv, -0.045, placket, 0.09);
        g.fillStyle = css(luma(spec.placket ?? spec.colors[0]) > 0.6 ? 0x1b1d24 : 0xf2f4f8);
        for (const k of spec.buttons === 3 ? [0.26, 0.54, 0.82] : [0.36, 0.76]) {
          g.beginPath();
          g.arc(pv + placket * k, 0, 0.021, 0, Math.PI * 2);
          g.fill();
        }
      }
      const tips = bandsC.slice(1).reverse(); // tipping, outermost first
      for (const s of [-1, 1]) {
        const leaf: Pt[] = [
          [v0 - 0.06, s * reach],
          [tipV, s * reach * 0.46],
          [v0 + depth * 0.34, s * 0.03],
          [v0 - 0.06, s * 0.03],
        ];
        g.fillStyle = css(bandsC[0][0]);
        g.beginPath();
        leaf.forEach(([x, yy], i) => (i === 0 ? g.moveTo(x, yy) : g.lineTo(x, yy)));
        g.closePath();
        g.fill();
        // The leaf's free edge carries the tipping.
        tips.forEach(([c], i) => {
          const o = 0.012 + i * 0.023;
          g.strokeStyle = css(c);
          g.lineWidth = 0.019;
          g.lineJoin = 'miter';
          g.beginPath();
          g.moveTo(v0 - 0.06, s * (reach - o));
          g.lineTo(tipV - o * 1.5, s * (reach * 0.46 - o * 0.1));
          g.lineTo(v0 + depth * 0.34 + o * 0.2, s * (0.03 + o));
          g.stroke();
        });
        // A soft crease where the leaf folds, so it reads as cloth on cloth.
        g.strokeStyle = rgba(0x000000, luma(bandsC[0][0]) > 0.7 ? 0.2 : 0.3);
        g.lineWidth = 0.011;
        g.beginPath();
        g.moveTo(v0 + depth * 0.34, s * 0.03);
        g.lineTo(tipV, s * reach * 0.46);
        g.lineTo(v0 - 0.06, s * reach);
        g.stroke();
      }
    } else {
      // Crew: a round neck, the rib bowing forward onto the hood.
      const crewPath = (d: number, o: number): [number, number][] => {
        const out: [number, number][] = [];
        for (let i = 0; i <= 12; i++) {
          const a = (i / 12) * Math.PI;
          out.push([o + Math.sin(a) * d, -Math.cos(a) * reach]);
        }
        return out;
      };
      const { depth, v0 } = fitNeck(geo, Math.min(hood * 0.22, 0.28), NECK_V0, crewPath, reach, forceArc);
      const pts: Pt[] = [];
      for (let i = 0; i <= 28; i++) {
        const a = (i / 28) * Math.PI;
        pts.push([v0 + Math.sin(a) * depth, -Math.cos(a) * reach]);
      }
      rib(g, pts, [1, 0], t * 1.15, bandsC);
    }
  });
}
function paintCuffs(v: LiveryViews, geo: Geo, spec: KitSpec): void {
  const counter = spec.pattern === 'quarters';
  if (spec.cuffs === undefined && !counter) return;
  const { L } = geo.fit;
  const t = 0.09;
  const mid = geo.cabin ? (geo.cowl + geo.tailGlass) / 2 : L / 2;
  if (geo.arches.length === 0) {
    if (spec.cuffs === undefined) return;
    // Open wheels: the cuff is a band at the tail, its hem toward the tail.
    // M153: on the flank only: across the tail top it crossed the worn
    // stripes into a '+' (the bike, re-review).
    const x = 0.93 * L;
    const bandsQ = spec.cuffBands ?? [[spec.cuffs, 1]];
    // ...and across the tail top on a plain shirt, its only accent from the
    // chase camera (River Plate's bike read all white from behind, round 2).
    const striped = spec.pattern === 'stripes' || spec.pattern === 'hoops';
    for (const g of striped ? [v.side] : [v.side, v.top]) rib(g, [[x, -BIG], [x, BIG]], [-1, 0], t * 1.2, bandsQ as Band[]);
    return;
  }
  for (const a of geo.arches) {
    // The eight-piece shirt counter-changes its cuffs: each arch lip wears
    // the other half's colour.
    const bandsQ: Band[] = counter
      ? [[a.x < mid ? spec.colors[1] : spec.colors[0], 1]]
      : ((spec.cuffBands as Band[] | undefined) ?? [[spec.cuffs!, 1]]);
    const total = bandsQ.reduce((p, b) => p + b[1], 0);
    let at = 0;
    const g = v.side;
    const hem = archEdge(geo.fit, a);
    for (const [color, weight] of bandsQ) {
      const w = (weight / total) * t;
      g.strokeStyle = css(color);
      g.lineWidth = w + 0.004;
      g.beginPath();
      // The hem is the arch lip: bands stack outward from it.
      g.arc(a.x, a.y, hem + at + w / 2, Math.PI, Math.PI * 2);
      g.stroke();
      at += w;
    }
  }
}

/** Raglan piping: a hairline from each end of the collar out to the arch
 *  (the seam that runs neck → armpit), on the hood and the deck. */
function paintPiping(v: LiveryViews, geo: Geo, spec: KitSpec): void {
  if (spec.piping === undefined || !geo.cabin) return;
  const { W } = geo.fit;
  const half = W / 2;
  for (const place of ['hood', 'deck'] as PanelPlace[]) {
    inPanel(v, geo, place, (g) => {
      g.strokeStyle = css(spec.piping!);
      g.lineWidth = 0.024;
      for (const s of [-1, 1]) {
        g.beginPath();
        g.moveTo(COLLAR_T * 1.7, s * half * 0.52);
        g.lineTo(0.7, s * half * 1.08);
        g.stroke();
      }
    });
  }
  // On the doors the same seam falls from the glass to each arch.
  for (const a of geo.arches) {
    const g = v.side;
    const toward = a.x < geo.fit.L / 2 ? 1 : -1;
    g.strokeStyle = css(spec.piping);
    g.lineWidth = 0.022;
    g.beginPath();
    g.moveTo(a.x + toward * a.r * 2.5, geo.beltY + COLLAR_T * 1.5);
    g.lineTo(a.x + toward * a.r * 1.0, a.y - a.r * 1.12);
    g.stroke();
  }
}

/** Solid sleeves (Olympiacos' red arms): the fenders round each arch. */
function paintSleeves(v: LiveryViews, geo: Geo, spec: KitSpec): void {
  if (spec.sleeves === undefined || spec.pattern === 'quarters') return;
  const g = v.side;
  g.fillStyle = css(spec.sleeves);
  if (geo.arches.length === 0) return;
  const flank = geo.fit.H - geo.beltY;
  for (const a of geo.arches) {
    // M153: a sleeve is the FENDER round its own arch. It stays under the
    // shoulder and off the doors (on the pickup's and the monster truck's
    // big arches the old 1.95x domes met in the middle and the striped
    // shirt never showed on the side).
    // M153: never smaller than the cuff it carries plus a real sleeve: on the
    // sports car's and the monster truck's short flanks the shoulder cap
    // squeezed it to 1.1x the tyre, the white cuff covered all of it and
    // Olympiacos lost its red arms (re-review). It may then reach the
    // shoulder line, never above it.
    // ...and never a whole panel: at most ~0.4 m of sleeve past the cuff (on
    // the pickup's long fender and bed side the 1.75x dome covered the whole
    // panel and the striped shirt showed on the doors only, round 2).
    const cuffOut = archEdge(geo.fit, a) + 0.09 * 1.4;
    let r = Math.min(a.r * 1.75, cuffOut + 0.4);
    r = Math.min(r, a.y - (geo.beltY + flank * 0.12));
    r = Math.max(r, Math.min(cuffOut + 0.18, a.y - geo.beltY - 0.03), a.r * 1.1);
    // Fore and aft the sleeve always reaches past its cuff: where the arch
    // climbs almost to the shoulder (the muscle car) there is no room above
    // the cuff, and a round dome stayed hidden under it (round 2).
    const rx = Math.max(r, cuffOut + 0.22);
    const front = a.x < geo.fit.L / 2;
    g.save();
    g.beginPath();
    if (front) g.rect(-BIG, -BIG, BIG + geo.door0, 2 * BIG);
    else g.rect(geo.door1, -BIG, BIG, 2 * BIG);
    g.clip();
    g.beginPath();
    g.moveTo(a.x - rx, geo.fit.H + 0.1);
    g.lineTo(a.x - rx, a.y - r * 0.2);
    g.quadraticCurveTo(a.x - rx, a.y - r, a.x, a.y - r);
    g.quadraticCurveTo(a.x + rx, a.y - r, a.x + rx, a.y - r * 0.2);
    g.lineTo(a.x + rx, geo.fit.H + 0.1);
    g.closePath();
    g.fill();
    g.restore();
  }
}

export interface KitLivery {
  paint: LiveryPainter;
  /** The far flank reads the side painting nose ↔ tail (halved shirts). */
  mirror: boolean;
  /** Showpiece wraps also paint a relief / glow / gloss atlas. */
  data?: boolean;
  /** Atlas edge in pixels (default 1024). */
  res?: number;
  /** Relief and glow strengths for the data atlas. */
  bump?: number;
  glow?: number;
  /** The lacquer over the paint (default: the full showroom clearcoat). */
  clearcoat?: number;
}

export function kitLivery(spec: KitSpec): KitLivery {
  if (spec.pattern === 'dragon') {
    // Phones paint (and hold) the wrap at half the size: a quarter of the
    // pixels to synthesise, and it is still sharper than the screen shows.
    const phone = typeof window !== 'undefined' && window.matchMedia?.('(pointer: coarse)').matches === true;
    // A thin lacquer only: under the full showroom clearcoat the hide mirrors
    // the sky and the roof reads grey from the chase camera (measured on
    // track). The scales' own gloss comes from the data atlas.
    return { paint: dragonPainter, mirror: false, data: true, res: phone ? 1024 : 2048, bump: 1.6, glow: 1.2, clearcoat: 0.3 };
  }
  return {
    mirror: spec.pattern === 'quarters',
    paint: (v) => {
      const geo = geoOf(v.fit);
      paintField(v, geo, spec);
      bandedHaunches(v, geo, spec);
      paintShadow(v, geo, spec);
      paintSleeves(v, geo, spec);
      paintYoke(v, geo, spec);
      paintPiping(v, geo, spec);
      paintCuffs(v, geo, spec);
      paintCollar(v, geo, spec);
    },
  };
}
